import React, { useState } from "react";
import ReactGA from "react-ga4";
import {
  IconMapPin,
  IconBriefcase,
  IconShare,
  IconCheck,
  IconArrowRight,
} from "@tabler/icons-react";
import { getJobLocationLabel, getJobTypeLabel } from "../utils/jobsUtils";

/**
 * A single job listing card: title + Share, a meta row (location + job type),
 * and a "View Details" CTA that links out to the Keka career portal.
 * The CTA is hidden when the job has no `careerPortalUrl`.
 */
const JobCard = ({ job }) => {
  const [copied, setCopied] = useState(false);

  const locationLabel = getJobLocationLabel(job);
  const typeLabel = getJobTypeLabel(job.jobType);
  const portalUrl = job.careerPortalUrl;

  const handleShare = async () => {
    ReactGA.event("careers_job_share", { job_id: job.id, job_title: job.title });
    // Share the job's portal link; fall back to the current page if absent.
    const shareUrl = portalUrl || window.location.href;
    const shareData = { title: job.title, url: shareUrl };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
        return;
      }
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(shareUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 1600);
      }
    } catch (err) {
      // User dismissed the share sheet or clipboard was blocked — no-op.
    }
  };

  return (
    <div className="jobCard">
      <div className="jobCardTop">
        <h3 className="jobCardTitle">{job.title}</h3>
        <button
          type="button"
          className="jobShareBtn"
          onClick={handleShare}
          aria-label={`Share ${job.title}`}
        >
          {copied ? <IconCheck size={18} stroke={2} /> : <IconShare size={18} stroke={2} />}
          <span className="jobShareLabel">{copied ? "Copied!" : "Share"}</span>
        </button>
      </div>

      <div className="jobCardMeta">
        <span className="jobMetaItem">
          <IconMapPin size={16} stroke={2} />
          {locationLabel}
        </span>
        <span className="jobMetaItem">
          <IconBriefcase size={16} stroke={2} />
          {typeLabel}
        </span>
      </div>

      <div className="jobCardCta">
        {portalUrl && (
          <a
            className="jobViewDetailsBtn"
            href={portalUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() =>
              ReactGA.event("careers_view_details_click", {
                job_id: job.id,
                job_title: job.title,
              })
            }
          >
            View Details
            <IconArrowRight size={18} stroke={2.5} />
          </a>
        )}
      </div>
    </div>
  );
};

export default JobCard;
